export type GamificationResultCaptureMetadata = {
  readonly route_template_id?: string;
  readonly source_locale?: string;
  readonly source_page?: string;
  readonly source_template?: string;
  readonly service_line?: string;
  readonly content_theme?: string;
  readonly tool_key?: string;
  readonly result_tier?: string;
  readonly budget_stage_bucket?: string;
  readonly vertical_18plus?: boolean;
  readonly vertical_18plus_reason?: string;
  readonly marketing_compliance_gate?:
    | "unknown"
    | "pending"
    | "passed"
    | "blocked"
    | "not_applicable"
    | "not_required"
    | string;
};

export const GAMIFICATION_CRM_RESULT_CAPTURE_KEYS = [
  "route_template_id",
  "source_locale",
  "source_page",
  "source_template",
  "service_line",
  "content_theme",
  "tool_key",
  "result_tier",
  "budget_stage_bucket",
  "vertical_18plus",
  "vertical_18plus_reason",
  "marketing_compliance_gate",
] as const satisfies readonly (keyof GamificationResultCaptureMetadata)[];

const MAX_SAFE_VALUE_LENGTH = 160;
const SAFE_TOKEN_PATTERN = /^[a-z0-9_.:/()[\]-]+$/i;

function cleanValue(value: unknown): string | boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim().slice(0, MAX_SAFE_VALUE_LENGTH);

  if (!trimmed || /[?&=]/.test(trimmed)) {
    return undefined;
  }

  return SAFE_TOKEN_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function sanitizeGamificationResultCapture(
  metadata: GamificationResultCaptureMetadata,
): GamificationResultCaptureMetadata {
  const safeEntries = GAMIFICATION_CRM_RESULT_CAPTURE_KEYS.flatMap((key) => {
    const value = cleanValue(metadata[key]);
    return value === undefined ? [] : [[key, value] as const];
  });

  return Object.fromEntries(safeEntries) as GamificationResultCaptureMetadata;
}

export function toGamificationHiddenFields(metadata: GamificationResultCaptureMetadata): Readonly<Record<string, string>> {
  const safe = sanitizeGamificationResultCapture(metadata);

  return Object.fromEntries(
    Object.entries(safe).map(([key, value]) => [key, typeof value === "boolean" ? String(value) : value]),
  );
}

export function toGamificationTrackingFields(metadata: GamificationResultCaptureMetadata): Readonly<Record<string, string | boolean>> {
  const safe = sanitizeGamificationResultCapture(metadata);
  const allowedTrackingKeys = new Set([
    "route_template_id",
    "source_locale",
    "service_line",
    "content_theme",
    "tool_key",
    "result_tier",
    "budget_stage_bucket",
    "vertical_18plus",
    "vertical_18plus_reason",
    "marketing_compliance_gate",
  ]);

  return Object.fromEntries(Object.entries(safe).filter(([key]) => allowedTrackingKeys.has(key)));
}
